import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { EnvConfigLoaderService } from '@app/shared/services/app/env-config-loader.service';

interface CheckResult {
  isAvailable: boolean;
  reason?: string;
}

interface Plan {
  id: number;
  name: string;
  slug: string;
  description?: string;
  monthlyPricePerUser: number;
  annualPricePerUser: number;
  currency: string;
  trialDays: number;
  maxUsers?: number;
}

interface SignupResult {
  success: boolean;
  error?: string;
  requestId?: number;
}

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {
  signupForm!: FormGroup;
  plans: Plan[] = [];
  isSubmitting = false;
  submitSuccess = false;
  submitError = '';

  emailStatus: 'idle' | 'checking' | 'available' | 'taken' = 'idle';
  emailMessage = '';
  slugStatus: 'idle' | 'checking' | 'available' | 'taken' = 'idle';
  slugMessage = '';

  private emailCheck$ = new Subject<string>();
  private slugCheck$ = new Subject<string>();

  private adminApiUrl: string;
  private env = 'dev';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    envConfig: EnvConfigLoaderService
  ) {
    this.adminApiUrl = (envConfig as any)?.config?.env?.adminApiUrl || '';
  }

  ngOnInit(): void {
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      companySlug: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern(/^[a-z0-9]+$/)]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      planSlug: ['', [Validators.required]]
    });

    this.loadPlans();
    this.setupEmailValidation();
    this.setupSlugValidation();
  }

  private loadPlans(): void {
    this.http.get<Plan[]>(`${this.adminApiUrl}/api/${this.env}/signup/plans`)
      .subscribe({
        next: plans => this.plans = plans,
        error: () => {}
      });
  }

  private setupEmailValidation(): void {
    this.emailCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(email => {
        if (!email || !email.includes('@')) return of(null);
        this.emailStatus = 'checking';
        return this.http.get<CheckResult>(
          `${this.adminApiUrl}/api/${this.env}/signup/check-email?email=${encodeURIComponent(email)}`
        );
      })
    ).subscribe(result => {
      if (!result) return;
      this.emailStatus = result.isAvailable ? 'available' : 'taken';
      this.emailMessage = result.reason || '';
    });
  }

  private setupSlugValidation(): void {
    this.slugCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(slug => {
        if (!slug || slug.length < 3) return of(null);
        this.slugStatus = 'checking';
        return this.http.get<CheckResult>(
          `${this.adminApiUrl}/api/${this.env}/signup/check-slug?slug=${encodeURIComponent(slug)}`
        );
      })
    ).subscribe(result => {
      if (!result) return;
      this.slugStatus = result.isAvailable ? 'available' : 'taken';
      this.slugMessage = result.reason || '';
    });
  }

  onEmailChange(): void {
    const email = this.signupForm.get('email')?.value;
    this.emailStatus = 'idle';
    this.emailMessage = '';
    if (email) this.emailCheck$.next(email);
  }

  onSlugChange(): void {
    const slug = this.signupForm.get('companySlug')?.value;
    this.slugStatus = 'idle';
    this.slugMessage = '';
    if (slug) this.slugCheck$.next(slug);
  }

  onCompanyNameBlur(): void {
    const companyName = this.signupForm.get('companyName')?.value;
    const currentSlug = this.signupForm.get('companySlug')?.value;

    // Auto-suggest slug from company name if slug is empty
    if (companyName && !currentSlug) {
      const suggested = companyName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      this.signupForm.patchValue({ companySlug: suggested });
      this.onSlugChange();
    }
  }

  onSubmit(): void {
    if (this.signupForm.invalid || this.emailStatus === 'taken' || this.slugStatus === 'taken') return;

    this.isSubmitting = true;
    this.submitError = '';

    this.http.post<SignupResult>(
      `${this.adminApiUrl}/api/${this.env}/signup`,
      this.signupForm.value
    ).subscribe({
      next: result => {
        this.isSubmitting = false;
        if (result.success) {
          this.submitSuccess = true;
        } else {
          this.submitError = result.error || 'An error occurred.';
        }
      },
      error: err => {
        this.isSubmitting = false;
        this.submitError = err.error?.error || 'An error occurred. Please try again.';
      }
    });
  }
}
